import type { MarketSummary } from '../types';
import { formatSaleDate, formatUsd, platformMeta } from '../utils/market';

interface MarketOverviewProps {
  summaries: MarketSummary[];
  selectedPlatform?: string;
  onSelectPlatform: (platform?: string) => void;
  onRetryUsd: () => void;
}

export default function MarketOverview({ summaries, selectedPlatform, onSelectPlatform, onRetryUsd }: MarketOverviewProps) {
  if (summaries.length === 0) return null;
  const unconvertedCount = summaries.reduce((total, summary) => total + (summary.unconvertedCount || 0), 0);
  const missingCurrencyCount = summaries.reduce((total, summary) => total + (summary.missingCurrencyCount || 0), 0);
  const retryableFxCount = Math.max(0, unconvertedCount - missingCurrencyCount);

  return (
    <section aria-labelledby="market-overview-heading">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-cyan">Market overview</p>
          <h2 id="market-overview-heading" className="mt-2 text-xl font-semibold tracking-[-0.035em] text-primary">All indexed market evidence</h2>
          <p className="mt-1 text-xs text-secondary">Summary uses every indexed sold record for this card.</p>
        </div>
        <button
          onClick={() => onSelectPlatform(undefined)}
          className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${selectedPlatform ? 'border border-border bg-white text-secondary hover:border-cyan/40' : 'bg-ink text-white'}`}
        >
          All markets
        </button>
      </div>

      {retryableFxCount > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber/25 bg-amber/10 px-4 py-3" role="status">
          <p className="text-xs text-amber-900">{retryableFxCount} market sale(s) have raw money but are waiting for an FX rate.</p>
          <button onClick={onRetryUsd} className="rounded-xl border border-amber/30 bg-white px-4 py-2 text-xs font-bold text-amber-900">Retry market USD</button>
        </div>
      )}
      {missingCurrencyCount > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-background-secondary px-4 py-3" role="status">
          <p className="text-xs text-secondary">{missingCurrencyCount} sale(s) cannot be normalized because the source currency is missing.</p>
          <a href="#sold-evidence-heading" className="rounded-xl border border-border bg-white px-4 py-2 text-xs font-bold text-primary">Inspect raw evidence</a>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {summaries.map((summary) => {
          const meta = platformMeta(summary.platform);
          const isSelected = selectedPlatform === summary.platform;
          return (
            <button
              key={summary.platform}
              onClick={() => onSelectPlatform(isSelected ? undefined : summary.platform)}
              className={`rounded-[20px] border p-4 text-left transition-all ${isSelected ? 'border-cyan bg-cyan/5 shadow-glow' : 'border-border bg-white hover:-translate-y-0.5 hover:border-cyan/35 hover:shadow-panel'}`}
              aria-pressed={isSelected}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: meta.color }} />
                  <span className="text-sm font-bold text-primary">{meta.name}</span>
                </div>
                <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-secondary-light">{summary.saleCount} sales</span>
              </div>
              <div className="mt-5">
                <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-secondary-light">Average · USD · {summary.pricedCount}/{summary.saleCount} priced</span>
                <span className="price-numeral mt-1 block text-2xl font-bold tracking-[-0.04em] text-primary">{formatUsd(summary.averageUsd)}</span>
              </div>
              <div className="mt-4 flex items-end justify-between gap-3 border-t border-border/70 pt-3">
                <div>
                  <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-secondary-light">USD range</span>
                  <span className="mt-1 block text-[11px] font-semibold text-secondary">{formatUsd(summary.lowUsd)} – {formatUsd(summary.highUsd)}</span>
                </div>
                <span className="text-right text-[10px] text-secondary-light">Latest<br />{formatSaleDate(summary.latestSoldTime)}</span>
              </div>
              <p className="mt-3 text-[9px] font-semibold uppercase tracking-[0.08em] text-secondary-light">FX date · {summary.fxAsOf || 'Unavailable'}</p>
              {summary.unconvertedCount > 0 && (
                <p className="mt-3 text-[10px] leading-4 text-amber-700">
                  {summary.unconvertedCount - (summary.missingCurrencyCount || 0) > 0
                    ? `${summary.unconvertedCount - (summary.missingCurrencyCount || 0)} waiting for FX`
                    : ''}
                  {summary.unconvertedCount - (summary.missingCurrencyCount || 0) > 0 && (summary.missingCurrencyCount || 0) > 0 ? ' · ' : ''}
                  {(summary.missingCurrencyCount || 0) > 0 ? `${summary.missingCurrencyCount} missing currency` : ''}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
